import { useMemo } from 'react'
import type { JSX } from 'react'

import { FieldValues } from 'react-hook-form'

import { useAutomations } from '~/app/automations/useAutomations'
import { CurrencyAmount } from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { Pill } from '~/components/component-library/Pill'
import { SelectInput } from '~/components/component-library/SelectInput'
import {
  SmartAccountSelectInputBaseProps,
  SmartAccountSelectInputProps,
} from '~/components/component-library/SmartAccountSelectInput/types'
import { getBridgeTokenType } from '~/constants/addresses'
import { useActiveOrg } from '~/hooks/org'
import { useOrgBalances } from '~/hooks/useBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import { cn } from '~/utils/tailwind-utils'

export const SmartAccountSelectInput = <
  FormType extends FieldValues = FieldValues,
>({
  control,
  inputName,
  selectedOption,
  placeholder = 'Select an account',
  filteredAddresses = [],
  setValue,
  hideBalance = false,
  includeAutomations = false,
  includeBankAccounts,
  addressLayout,
  size = 'base',
  optional,
  requiredErrorMessage,
  disabled,
  disabledReason,
  ...groupProps
}: SmartAccountSelectInputProps<FormType>): JSX.Element => {
  const options = useSmartAccountSelectOptions({
    selectedOption,
    filteredAddresses,
    hideBalance,
    includeAutomations,
    includeBankAccounts,
    addressLayout,
    size,
  })

  if (control) {
    return (
      <SelectInput<IAddress, FormType>
        control={control}
        inputName={inputName}
        options={options}
        placeholder={placeholder}
        selectedOption={selectedOption}
        selectValue={setValue}
        size={size}
        optional={optional}
        requiredErrorMessage={requiredErrorMessage}
        disabled={disabled}
        disabledReason={disabledReason}
        {...groupProps}
      />
    )
  }

  return (
    <SelectInput<IAddress>
      options={options}
      placeholder={placeholder}
      selectedOption={selectedOption}
      selectValue={setValue}
      size={size}
      disabled={disabled}
      disabledReason={disabledReason}
      {...groupProps}
    />
  )
}

function useSmartAccountSelectOptions({
  selectedOption = undefined,
  filteredAddresses = [],
  hideBalance = false,
  includeAutomations = false,
  includeBankAccounts,
  addressLayout,
  size = 'base',
}: Partial<SmartAccountSelectInputBaseProps>) {
  const { org } = useActiveOrg()
  const { smartAccounts: orderedSmartAccounts, isLoading } = useSmartAccounts({
    orderedByValue: true,
  })
  const { automations, isLoading: isLoadingAutomations } = useAutomations({
    enabled: includeAutomations,
  })

  const filteredSmartAccounts = useMemo(
    () =>
      orderedSmartAccounts.filter(
        (smartAccount) => !filteredAddresses.includes(smartAccount.address),
      ),
    [filteredAddresses, orderedSmartAccounts],
  )

  const filteredAutomations = useMemo(() => {
    if (!includeAutomations) return []
    return automations.filter(
      (automation) =>
        automation.isActive &&
        automation.accountAddress &&
        !filteredAddresses.includes(automation.accountAddress as IAddress),
    )
  }, [includeAutomations, automations, filteredAddresses])

  const filteredBankAccounts = useMemo(() => {
    if (!includeBankAccounts) return []
    const accounts = org?.bridge?.externalAccounts ?? []
    if (includeBankAccounts === 'all') return accounts
    const tokenType = includeBankAccounts === 'usd' ? 'usdc' : 'eurc'
    return accounts.filter(
      (account) =>
        getBridgeTokenType(
          account.liquidationChainId,
          Address.format(account.liquidationToken),
        ) === tokenType,
    )
  }, [includeBankAccounts, org?.bridge?.externalAccounts])

  const { balances } = useOrgBalances(org!.id)

  return useMemo(() => {
    const allAddresses = [
      ...filteredSmartAccounts.map((sa) => sa.address),
      ...filteredAutomations.map((a) => a.accountAddress as IAddress),
      ...filteredBankAccounts.map((b) => b.liquidationAddress),
    ]
    const validSelection = allAddresses.some(
      (address) => address === selectedOption,
    )

    if (
      !validSelection &&
      !!selectedOption &&
      !isLoading &&
      !isLoadingAutomations
    ) {
      // Selection belongs to a previous org when switching orgs.
      // Return nothing until the component remounts with the new org's accounts.
      return []
    }

    // AddressDisplay resolves the name and avatar for every account type
    // (smart accounts, automations, and bank accounts) from the address alone,
    // so each row is rendered the same way. Automations get a badge since
    // their names alone don't distinguish them, and bank accounts have no
    // on-chain balance to show, so the balance is omitted for them.
    const buildOption = ({
      address,
      showBalance,
      isAutomation = false,
    }: {
      address: IAddress
      showBalance: boolean
      isAutomation?: boolean
    }) => ({
      value: address,
      label: (
        <div className="flex w-full flex-1 items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <AddressDisplay
              address={address}
              layout={addressLayout ?? 'standard'}
              isInternal
              hidePopover
              size={size}
            />
            {isAutomation && <Pill label="Automation" size="small" />}
          </div>
          {showBalance && !hideBalance && (
            <span
              className={cn('text-secondary', {
                'text-sm': size === 'small',
              })}
            >
              <CurrencyAmount
                value={balances[address]?.totalValue ?? 0}
                layout="inline"
              />
            </span>
          )}
        </div>
      ),
    })

    return [
      ...filteredSmartAccounts.map((account) =>
        buildOption({ address: account.address, showBalance: true }),
      ),
      ...filteredAutomations.map((automation) =>
        buildOption({
          address: automation.accountAddress as IAddress,
          showBalance: true,
          isAutomation: true,
        }),
      ),
      ...filteredBankAccounts.map((account) =>
        buildOption({
          address: account.liquidationAddress,
          showBalance: false,
        }),
      ),
    ]
  }, [
    balances,
    filteredSmartAccounts,
    filteredAutomations,
    filteredBankAccounts,
    addressLayout,
    hideBalance,
    isLoading,
    isLoadingAutomations,
    selectedOption,
    size,
  ])
}

export type { SmartAccountSelectInputProps } from '~/components/component-library/SmartAccountSelectInput/types'
